import { memo, RefObject, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { IdentifierType } from '@weave/schema-gen-ts/dist/schemas/notification/v1/notification_api.pb';
import { VoicemailBox } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/voicemail.pb';
import {
  DeleteVoicemailResponse,
  SetVoicemailViewStatusResponse,
  ViewStatus,
} from '@weave/schema-gen-ts/dist/schemas/phone-exp/voicemail/voicemail.pb';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { UseInfiniteQueryResult, UseMutateFunction } from 'react-query';
import { VirtuosoHandle } from 'react-virtuoso';
import { NotificationQueries } from '@frontend/api-notifications';
import { VoiceMailboxApi } from '@frontend/api-voicemail-boxes';
import { VoicemailTypes } from '@frontend/api-voicemails';
import { AdaptoActions, FloatingActionBar } from '@frontend/adapto-actions';
import { EmptyStateConfig, InfinitePaginatedList, RenderListItemProps } from '@frontend/components';
import { useGetDisplayName } from '@frontend/get-display-name';
import { useTranslation } from '@frontend/i18n';
import { ScopedLocationFilterMenu } from '@frontend/location-filter-menu';
import { CachedAudioScrubber } from '@frontend/phone-panels';
import { Photos } from '@frontend/photos';
import { useQueueStore } from '@frontend/queue';
import { useScopedQuery, useWeTags } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  Avatar,
  BaseFieldProps,
  Button,
  PhoneOutgoingIcon,
  SearchField,
  useFormField,
  useModalControl,
  Text,
  styles,
  TextLink,
  ListRow,
  Truncated,
} from '@frontend/design-system';
import { VoicemailFiltersType } from '../../hooks/use-phone-config-state-store';
import { queryKeys } from '../../query-keys';
import { useVoicemailDetailsShallowStore } from '../../stores/voicemail-details-store';
import { VoicemailInfiniteQueryData, VoicemailTableDataRow } from '../all-calls/types';
import { TranscriptionContext } from '../voicemail-details/types';
import { getTranscriptionUniqueKey } from '../voicemail-details/utils';
import { useSyncDetailsPanel } from './panel-sync';
import { VoicemailsTrayFilters } from './vm-filters';
import { VoicemailActionsGetterProps } from './voicemail-actions';
import { TagColumn } from './voicemail-table-view-components';
import { hasVMFilterChanged } from './voicemail-utils';

export const SmallView = memo(
  ({
    getVoicemailActions,
    selectedLocationIds,
    setFilters,
    filters,
    infiniteQueryProps,

    defaultFilters,
    voicemailBoxList,
    mappedTags,

    searchHandler,
    // emptyState,

    listHeight,
    panelProps,
  }: {
    getVoicemailActions: (props: VoicemailActionsGetterProps) => (React.ReactElement | null)[];
    selectedLocationIds: string[];
    setFilters: (filters: VoicemailFiltersType) => void;
    filters: VoicemailFiltersType;
    infiniteQueryProps: UseInfiniteQueryResult<VoicemailInfiniteQueryData, unknown>;

    defaultFilters: VoicemailFiltersType;
    voicemailBoxList: VoicemailBox[];
    mappedTags: Record<string, Tag>;

    searchHandler: (searchText: string) => void;
    emptyState: EmptyStateConfig;

    listHeight?: number;
    panelProps: PanelProps;
  }) => {
    const { t } = useTranslation('calls');
    const searchField = useFormField(
      { type: 'text', placeholder: t('Search name or 10-digit number'), value: filters.filterOptions.callerName },
      [filters.filterOptions.callerName]
    );

    const [shouldShowSearch, setShouldShowSearch] = useState(false);

    const {
      instanceId,
      rows,
      currentRowId,
      setPageSize,
      setUpdateReadStatus,
      setDeleteVoicemail,
      setInstanceId,
      setRows,
      setToggleRowSelected,
      setVoicemailsQueryKey,
      setSectionFocus,
      setUserHasAccess,
      setIsDetached,
    } = useVoicemailDetailsShallowStore(
      'instanceId',
      'rows',
      'currentRowId',
      'setPageSize',
      'setUpdateReadStatus',
      'setDeleteVoicemail',
      'setInstanceId',
      'setRows',
      'setToggleRowSelected',
      'setVoicemailsQueryKey',
      'setSectionFocus',
      'setUserHasAccess',
      'setIsDetached'
    );

    const [selectedIndex, setSelectedIndex] = useState(() =>
      currentRowId ? rows.findIndex((row) => getTranscriptionUniqueKey(row) === currentRowId) : -1
    );

    const allDataRows = useMemo(() => {
      if (!infiniteQueryProps.data?.pages) return [];
      return infiniteQueryProps.data.pages.flatMap((page) => page.rows || []);
    }, [infiniteQueryProps]);

    const paginatedListRef = useRef<VirtuosoHandle>(null);

    const toggleRowSelected = useCallback((_: string, selected: boolean, index?: number) => {
      if (!selected) {
        setSelectedIndex(-1);
        return;
      }

      if (index === undefined) {
        return;
      }
      setSelectedIndex(index);
      paginatedListRef.current?.scrollToIndex({
        index: index,
        align: 'start',
        behavior: 'smooth',
      });
    }, []);

    useSyncDetailsPanel({
      shouldSync: instanceId !== 'table',
      instanceId: 'table',
      toggleRowSelected,
      getNextPage: infiniteQueryProps.fetchNextPage,
      markCurrentRowSelected: () => {
        const existingRowIndex = currentRowId
          ? rows.findIndex((row) => getTranscriptionUniqueKey(row) === currentRowId)
          : -1;

        if (existingRowIndex >= 0) {
          toggleRowSelected('', true, existingRowIndex);
        }
      },
    });

    const setupDetailsPanel = useCallback(() => {
      setPageSize(25);
      setUpdateReadStatus(panelProps.updateReadStatus);
      setDeleteVoicemail(panelProps.deleteVoicemail);
      setInstanceId('list-view');
      setRows(allDataRows);
      setToggleRowSelected(toggleRowSelected);
      setVoicemailsQueryKey(panelProps.voicemailsQueryKey);
      setSectionFocus(panelProps.sectionFocus);
      setUserHasAccess(true);
      setIsDetached(false);
    }, [
      allDataRows,
      panelProps.deleteVoicemail,
      panelProps.sectionFocus,
      panelProps.updateReadStatus,
      panelProps.voicemailsQueryKey,
      setDeleteVoicemail,
      setInstanceId,
      setPageSize,
      setRows,
      setSectionFocus,
      setIsDetached,
      setToggleRowSelected,
      setUpdateReadStatus,
      setUserHasAccess,
      setVoicemailsQueryKey,
      toggleRowSelected,
    ]);

    const showPanel = useCallback(
      ({ listItem, index }: { listItem: VoicemailTableDataRow; index: number }) => {
        setupDetailsPanel();
        invokeShowPanel({ listItem, index });
      },
      [setupDetailsPanel]
    );

    const renderListItem = useMemo(
      () =>
        getRenderListItem({
          getVoicemailActions,
          selectedIndex,
          showPanel,
          panelProps,
        }),
      [getVoicemailActions, selectedIndex, showPanel, panelProps]
    );

    return (
      <div>
        <div
          css={{
            padding: theme.spacing(0, 2, 2),
            display: 'grid',
            gap: theme.spacing(1),
            borderBottom: `1px solid ${theme.colors.neutral20}`,
          }}
        >
          {shouldShowSearch ? (
            <SearchBar
              label={t('Done')}
              onDismissClick={() => setShouldShowSearch(false)}
              field={searchField}
              searchHandler={(searchText) => {
                searchHandler(searchText);
              }}
            />
          ) : (
            <ActionBar
              selectedLocations={selectedLocationIds}
              onLocationChange={(locations) => {
                setFilters({
                  ...filters,
                  locationIds: locations,
                });
              }}
              onSearchClick={() => setShouldShowSearch(true)}
              showSearchBadge={!!filters.filterOptions.callerName}
              showFilterBadge={hasVMFilterChanged(filters, defaultFilters)}
              filterProps={{
                defaultFilters,
                filters,
                mailboxes: voicemailBoxList,
                onChange: setFilters,
                isMulti: selectedLocationIds.length > 1,
                mappedTags,
                resetFilters: () => {
                  setFilters(defaultFilters);
                },
              }}
            />
          )}
        </div>
        <div>
          <InfinitePaginatedList
            initialTopMostItemIndex={selectedIndex === -1 ? 0 : selectedIndex}
            virtuosoRef={paginatedListRef}
            height={listHeight}
            infiniteQueryProps={infiniteQueryProps}
            renderListItem={renderListItem}
          />
        </div>
      </div>
    );
  }
);
SmallView.displayName = 'VoicemailSmallView';

type PanelProps = {
  updateReadStatus: UseMutateFunction<SetVoicemailViewStatusResponse, unknown, VoicemailTypes.StatusPayload, unknown>;
  deleteVoicemail: UseMutateFunction<DeleteVoicemailResponse, unknown, VoicemailTypes.DeletePayload, void>;
  voicemailsQueryKey: string[];
  sectionFocus: TranscriptionContext['sectionFocus'];
};

const invokeShowPanel = ({ listItem, index }: { listItem: VoicemailTableDataRow; index: number }) => {
  const { enqueue } = useQueueStore.getState();
  enqueue('voicemail-details', {
    payload: {
      type: 'open-with-source',
      data: listItem,
      id: getTranscriptionUniqueKey(listItem),
      index: index,
    },
  });
};

const VoicemailListItem = memo(
  ({
    listItem,
    index,
    getVoicemailActions,
    isSelected,
    showPanel,
    panelProps: { updateReadStatus, voicemailsQueryKey },
  }: {
    listItem: VoicemailTableDataRow;
    index: number;
    getVoicemailActions: (props: VoicemailActionsGetterProps) => (React.ReactElement | null)[];
    isSelected: boolean;
    showPanel: (props: { listItem: VoicemailTableDataRow; index: number }) => void;
    panelProps: PanelProps;
  }) => {
    const [isEditing, setIsEditing] = useState(false);
    const { locationIds } = listItem;
    const { firstName, lastName } = useGetDisplayName({
      person: {
        firstName: listItem.firstName,
        lastName: listItem.lastName,
        preferredName: listItem.preferredName,
      },
    });
    const markAsReadMutation = NotificationQueries.useMarkNotificationsAsRead();
    const textRef = useRef<HTMLSpanElement>(null);
    const showCompactListRow = useWeTags('compact-list-row');
    const { data: transcription } = useScopedQuery({
      queryKey: queryKeys.voicemailTranscript(listItem.voicemailId ?? ''),
      queryFn: async () => {
        const response = await VoiceMailboxApi.getTranscription(listItem.voicemailId ?? '');
        return response;
      },
      retry: false,
      enabled: !!listItem,
      staleTime: Infinity,
      cacheTime: Infinity,
    });

    const addTag = () => {
      setIsEditing(true);
    };

    const { actions } = useMemo(() => {
      const allActions = getVoicemailActions({
        index,
        rowData: listItem,
        addTag,
      });

      allActions.splice(
        2,
        0,
        <AdaptoActions.Action
          icon='document-filled'
          key='view-vm-details-action'
          onClick={() => showPanel({ listItem, index })}
          label={'View Voicemail Details'}
          trackingId={'phone-portal-weave2.0-voicemails-view-details'}
        />
      );

      return { actions: allActions };
    }, [getVoicemailActions, index, listItem, showPanel]);

    const [willOverflow, setWillOverflow] = useState(false);

    useLayoutEffect(() => {
      if (textRef.current && transcription?.text) {
        // Create a temporary element to measure text width
        const tempElement = document.createElement('span');
        tempElement.style.visibility = 'hidden';
        tempElement.style.position = 'absolute';
        tempElement.style.whiteSpace = 'nowrap';
        tempElement.style.fontSize = getComputedStyle(textRef.current).fontSize;
        tempElement.style.fontFamily = getComputedStyle(textRef.current).fontFamily;
        tempElement.style.fontWeight = getComputedStyle(textRef.current).fontWeight;
        tempElement.textContent = transcription.text;

        document.body.appendChild(tempElement);
        const textWidth = tempElement.getBoundingClientRect().width;
        document.body.removeChild(tempElement);

        const containerWidth = textRef.current.parentElement?.getBoundingClientRect().width || 0;
        setWillOverflow(textWidth > containerWidth);
      }
    }, [transcription?.text]);

    const markNotificationAsRead = useCallback(
      (voicemailId: string) =>
        markAsReadMutation.mutate({
          notificationType: NotificationType.VOICEMAIL,
          identifiers: [
            {
              key: 'voicemailId',
              val: voicemailId,
              type: IdentifierType.STRING,
            },
          ],
        }),
      []
    );

    const updateRead = useCallback(() => {
      if (!listItem.readAt) {
        markNotificationAsRead(listItem.forwardedMessageId || listItem.channelId);
      }
      updateReadStatus({
        index,
        voicemailId: listItem?.forwardedMessageId || listItem.channelId,
        status: ViewStatus.VIEW_STATUS_READ,
      });
    }, [
      index,
      listItem.channelId,
      listItem?.forwardedMessageId,
      updateReadStatus,
      listItem.readAt,
      markNotificationAsRead,
    ]);

    if (showCompactListRow) {
      return (
        <FloatingActionBar>
          <FloatingActionBar.Actions>
            {actions.length > 0 && <Actions actions={actions} size='small' />}
          </FloatingActionBar.Actions>
          <FloatingActionBar.Content>
            <ListRow
              css={{
                padding: theme.spacing(1.5),
                backgroundColor: isSelected ? theme.colors.neutral5 : undefined,
                cursor: 'pointer',
              }}
              onClick={() => showPanel({ listItem, index })}
            >
              <ListRow.Lead>
                <Photos.ContactProfilePhoto
                  css={{ gridArea: 'photo', gridRow: 'first' }}
                  locationId={locationIds[0]}
                  personId={listItem.contactId || ''}
                  name={listItem.contactName}
                  disableClick={!listItem.contactId}
                >
                  {!listItem.readAt && <Avatar.Dot variant='primary' />}
                </Photos.ContactProfilePhoto>
              </ListRow.Lead>
              <ListRow.Content>
                <ListRow.Content.Title
                  css={{
                    whiteSpace: 'pre-line',
                    fontWeight: 'inherit',
                    display: 'flex',
                  }}
                  isCompactMode
                >
                  <NamePlate
                    firstName={firstName}
                    lastName={lastName}
                    potentialName={listItem.callerName && !isNaN(+listItem?.callerName) ? '' : listItem?.callerName}
                    css={{ gridArea: 'name', gridRow: 'first' }}
                    size='large'
                  />
                </ListRow.Content.Title>
                <ListRow.Content.Subtitle
                  css={{
                    whiteSpace: 'pre-line',
                    fontWeight: 'inherit',
                    display: 'flex',
                    flexDirection: 'column',
                    fontSize: theme.fontSize(12),
                  }}
                  isCompactMode
                >
                  <Truncated>
                    {listItem.dateTime} • {listItem.contactNumber}
                  </Truncated>
                  <div css={{ display: 'flex', flexDirection: 'column', fontSize: theme.fontSize(14) }}>
                    <CachedAudioScrubber
                      filePath={listItem.mediaFilePath}
                      mediaId={`voicemail-${listItem.mediaId}`}
                      onPlay={updateRead}
                      isCompactMode
                    />
                    {transcription?.text &&
                      (willOverflow ? (
                        <OverflowingText textRef={textRef} text={transcription.text} markAsRead={updateRead} />
                      ) : (
                        <Text ref={textRef} css={styles.truncate} as='span'>
                          {transcription.text}
                        </Text>
                      ))}
                  </div>
                </ListRow.Content.Subtitle>
              </ListRow.Content>
            </ListRow>
          </FloatingActionBar.Content>
        </FloatingActionBar>
      );
    }
    return (
      <div
        css={{
          display: 'grid',
          padding: theme.spacing(2),
          gridTemplateAreas: '"photo name actions" ". details details" ". meta meta" ". tags ."',
          gridTemplateColumns: 'auto 1fr auto',
          gridTemplateRows:
            '[first-start] auto [first-end] 0px [second-start] auto [second-end] 8px [third-start] auto [third-end] 8px [fourth-start] auto [fourth-end]',
          columnGap: theme.spacing(1),
          alignItems: 'center',
          borderBottom: `1px solid ${theme.colors.neutral20}`,
          backgroundColor: isSelected ? theme.colors.neutral5 : undefined,
        }}
      >
        <Photos.ContactProfilePhoto
          css={{ gridArea: 'photo', gridRow: 'first' }}
          locationId={locationIds[0]}
          personId={listItem.contactId || ''}
          name={listItem.contactName}
          disableClick={!listItem.contactId}
        >
          {!listItem.readAt && <Avatar.Dot variant='primary' />}
        </Photos.ContactProfilePhoto>
        <NamePlate
          firstName={firstName}
          lastName={lastName}
          potentialName={listItem.callerName && !isNaN(+listItem?.callerName) ? '' : listItem?.callerName}
          css={{ gridArea: 'name', gridRow: 'first' }}
        />

        {actions.length > 0 && <Actions actions={actions} size='large' />}
        <div
          css={{ gridArea: 'details', display: 'grid', alignItems: 'center', gap: theme.spacing(1), gridRow: 'second' }}
        >
          <div
            css={{
              display: 'grid',
              gridTemplateColumns: 'max-content max-content max-content',
              alignItems: 'center',
              gap: theme.spacing(1),
            }}
          >
            <Text size='medium'>{listItem.dateTime}</Text>•<Text size='medium'>{listItem.contactNumber}</Text>
          </div>
          <CachedAudioScrubber filePath={listItem.mediaFilePath} mediaId={listItem.mediaId} onPlay={updateRead} />
        </div>
        <div css={{ gridArea: 'meta', display: 'grid', alignItems: 'center', gap: theme.spacing(1), gridRow: 'third' }}>
          {transcription?.text &&
            (willOverflow ? (
              <OverflowingText textRef={textRef} text={transcription.text} markAsRead={updateRead} />
            ) : (
              <Text ref={textRef} css={styles.truncate} as='span'>
                {transcription.text}
              </Text>
            ))}
        </div>
        <div
          css={{ gridArea: 'tags', display: 'grid', alignItems: 'center', gap: theme.spacing(1), gridRow: 'fourth' }}
        >
          <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
            <TagColumn
              {...listItem}
              showPanel={() => showPanel({ listItem, index })}
              queryKey={voicemailsQueryKey}
              isEditing={isEditing}
              setIsEditing={setIsEditing}
            />
          </div>
        </div>
      </div>
    );
  }
);

VoicemailListItem.displayName = 'VoicemailListItem';

const getRenderListItem =
  ({
    getVoicemailActions,
    selectedIndex,
    showPanel,
    panelProps,
  }: {
    getVoicemailActions: (props: VoicemailActionsGetterProps) => (React.ReactElement | null)[];
    selectedIndex: number;
    showPanel: (props: { listItem: VoicemailTableDataRow; index: number }) => void;
    panelProps: PanelProps;
  }) =>
  // eslint-disable-next-line react/display-name
  ({ listItem, index }: RenderListItemProps<VoicemailTableDataRow>) => {
    return (
      <VoicemailListItem
        listItem={listItem}
        index={index}
        isSelected={index === selectedIndex}
        getVoicemailActions={getVoicemailActions}
        showPanel={showPanel}
        panelProps={panelProps}
      />
    );
  };

const OverflowingText = memo(
  ({ text, textRef, markAsRead }: { text: string; textRef: RefObject<HTMLSpanElement>; markAsRead: () => void }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { t } = useTranslation('calls');

    if (isExpanded) {
      return (
        <div
          css={{
            display: 'grid',
            gridTemplateRows: '1fr auto',
          }}
        >
          <Text ref={textRef} as='span' css={{ fontSize: 'inherit' }}>
            {text}
          </Text>
          <TextLink
            css={{ justifySelf: 'end' }}
            weight='bold'
            onClick={(e) => {
              setIsExpanded(false);
              e.stopPropagation();
            }}
          >
            {t('Less')}
          </TextLink>
        </div>
      );
    }

    return (
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: '1fr max-content',
          alignItems: 'center',
          gap: theme.spacing(1),
          width: '100%',
        }}
      >
        <Text
          ref={textRef}
          as='span'
          css={[
            styles.truncate,
            css`
              font-size: inherit;
            `,
          ]}
        >
          {text}
        </Text>
        <TextLink
          weight='bold'
          onClick={(e) => {
            setIsExpanded(true);
            markAsRead?.();
            e.stopPropagation();
          }}
        >
          {t('More')}
        </TextLink>
      </div>
    );
  }
);

OverflowingText.displayName = 'OverflowingText';

const Actions = ({
  className,
  actions,
  size,
}: {
  className?: string;
  actions: (React.ReactElement | null)[];
  size: 'small' | 'large';
}) => {
  return (
    <div className={className} css={{ display: 'grid', gridTemplateColumns: 'repeat(3, max-content)' }}>
      <AdaptoActions maxActions={3} size={size} iconColor='inherit'>
        {actions}
      </AdaptoActions>
    </div>
  );
};

Actions.displayName = 'Actions';

const NamePlate = ({
  firstName,
  lastName,
  potentialName,
  fallback,
  direction,
  className,
  size = 'medium',
}: {
  firstName?: string;
  lastName?: string;
  potentialName?: string;
  fallback?: string;
  direction?: 'outbound' | 'inbound';
  className?: string;
  size?: 'medium' | 'large';
}) => {
  const { t } = useTranslation('calls');
  let content;
  const hasDefinedName = firstName || lastName;

  if (hasDefinedName) {
    content = (
      <div>
        {!!firstName && (
          <Text as='span' size={size} weight='bold'>
            {firstName}
          </Text>
        )}

        {firstName && lastName ? ' ' : null}

        {!!lastName && (
          <Text as='span' size={size}>
            {lastName}
          </Text>
        )}
      </div>
    );
  } else if (potentialName) {
    content = (
      <Text as='span' css={{ fontStyle: 'italic' }} size={size}>
        {potentialName}
      </Text>
    );
  } else {
    content = (
      <Text as='span' size={size}>
        {fallback || t('Unknown')}
      </Text>
    );
  }

  return (
    <div
      className={className}
      css={{
        display: 'grid',
        gridTemplateColumns: 'max-content max-content',
        alignItems: 'center',
        gap: theme.spacing(1),
      }}
    >
      {content}
      {direction === 'outbound' && <PhoneOutgoingIcon size={16} />}
    </div>
  );
};

const SearchBar = ({
  label,
  onDismissClick,
  field,
  searchHandler,
}: {
  label: string;
  onDismissClick: () => void;
  field: BaseFieldProps<string>;
  searchHandler: (searchText: string) => void;
}) => {
  return (
    <div css={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: theme.spacing(1) }}>
      <SearchField
        {...field}
        autoFocus
        onChange={(e) => {
          field.onChange(e);

          if (!e) {
            return;
          }

          if ('target' in e) {
            searchHandler((e.target as HTMLInputElement).value);
          } else {
            searchHandler(e.value);
          }
        }}
        label=''
        name='search'
      />
      <Button variant='secondary' size='large' onClick={onDismissClick}>
        {label}
      </Button>
    </div>
  );
};

const ActionBar = ({
  selectedLocations,
  onLocationChange,
  onSearchClick,
  showSearchBadge,
  showFilterBadge,
  filterProps,
}: {
  selectedLocations: string[];
  onLocationChange: (locations: string[]) => void;
  onSearchClick: () => void;
  showFilterBadge: boolean;
  showSearchBadge: boolean;
  filterProps: {
    defaultFilters: VoicemailFiltersType;
    filters: VoicemailFiltersType;
    mailboxes: VoicemailBox[];
    onChange: (filters: VoicemailFiltersType) => void;
    isMulti: boolean;
    mappedTags: Record<string, Tag>;
    resetFilters: () => void;
  };
}) => {
  const { modalProps, triggerProps } = useModalControl();

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: selectedLocations.length > 1 ? '1fr auto auto' : 'auto auto auto 1fr',
        gridTemplateAreas: selectedLocations.length > 1 ? '"location search filter"' : '"location search filter ."',
      }}
    >
      <ScopedLocationFilterMenu
        selectedOptions={selectedLocations}
        trackingIdBase={'phone-weave2.0-voicemails'}
        onChange={onLocationChange}
        showClearAll={false}
        css={{ gridArea: 'location' }}
      />
      <Button
        variant='secondary'
        iconName='search'
        size='large'
        css={[
          { gridArea: 'search' },
          css`
            ::after {
              position: absolute;
              opacity: ${showSearchBadge ? 1 : 0};
              background: ${theme.colors.primary50};
              border-radius: 16px;
              border: 1.5px solid ${theme.colors.white};
              height: ${theme.spacing(1.5)};
              width: ${theme.spacing(1.5)};
              top: 6px;
              left: ${theme.spacing(3)};
              z-index: ${theme.zIndex.high};
              content: '';
              transition: border-color, opacity ease-in-out 250ms;
            }
            :hover::after,
            :focus::after {
              border-color: ${theme.colors.neutral10};
            }
          `,
        ]}
        onClick={onSearchClick}
      />
      <Button
        variant='secondary'
        iconName='filter-alt'
        size='large'
        css={[
          { gridArea: 'filter' },
          css`
            ::after {
              position: absolute;
              opacity: ${showFilterBadge ? 1 : 0};
              background: ${theme.colors.primary50};
              border-radius: 16px;
              border: 1.5px solid ${theme.colors.white};
              height: ${theme.spacing(1.5)};
              width: ${theme.spacing(1.5)};
              top: 6px;
              left: ${theme.spacing(3)};
              z-index: ${theme.zIndex.high};
              content: '';
              transition: border-color, opacity ease-in-out 250ms;
            }
            :hover::after,
            :focus::after {
              border-color: ${theme.colors.neutral10};
            }
          `,
        ]}
        {...triggerProps}
      />
      <VoicemailsTrayFilters {...filterProps} modalProps={modalProps} />
    </div>
  );
};
